import React, { useEffect, useState } from "react";
import { Input, FormGroup, Label, Button, Spinner } from "reactstrap";
import { useNavigate, useParams } from "react-router-dom";
import axios from "axios";
import { useSelector } from "react-redux";
import { toast } from "react-toastify";

const reviewCategoryType = [
  { value: "question", name: "Question" },
  { value: "stars", name: "Stars" },
  { value: "questionCategory", name: "Question Category" },
  { value: "spamQuestion", name: "Spam Question" },
  { value: "questionStarCategory", name: "Question Star Category" },
];

const needsParent = (type) =>
  ["stars", "questionCategory", "questionStarCategory"].includes(type);

const nameLabels = {
  question: "Question",
  stars: "Stars",
  questionCategory: "Question Category",
  spamQuestion: "Question",
  questionStarCategory: "Question Star Category",
};

const AddReviewCategory = () => {
  const { id } = useParams();
  const isEdit = Boolean(id);

  const [errors, setErrors] = useState({});
  const [questions, setQuestions] = useState([]);
  const [loading, setLoading] = useState(false);
  const [fetching, setFetching] = useState(isEdit);
  const [formData, setFormData] = useState({
    type: "",
    parent_id: "",
    name: "",
    description: "",
    category_color: "",
    status: 1,
  });

  const {
    AuthReducer: { auth },
  } = useSelector((state) => state);
  const navigate = useNavigate();

  useEffect(() => {
    if (!isEdit || !auth?.token) return;

    setFetching(true);
    axios
      .get(`/api/admin/v1/review-category/${id}`, {
        headers: { Authorization: `Bearer ${auth?.token}` },
      })
      .then((res) => {
        const category = res.data?.category;
        if (!category) {
          toast.error("Review category not found");
          navigate("/review-category");
          return;
        }
        setFormData({
          type: category.type || "",
          parent_id: category.parent_id ? String(category.parent_id) : "",
          name: category.name || "",
          description: category.description || "",
          category_color: category.category_color || "",
          status: category.status ?? 1,
        });
      })
      .catch((err) => {
        toast.error(err.response?.data?.message || "Failed to load category");
        navigate("/review-category");
      })
      .finally(() => setFetching(false));
  }, [id, isEdit, auth?.token, navigate]);

  useEffect(() => {
    if (!needsParent(formData.type) || !auth?.token) return;

    axios
      .get("/api/admin/v1/review-category-questions", {
        headers: { Authorization: `Bearer ${auth?.token}` },
      })
      .then((res) => setQuestions(res.data?.questions || []))
      .catch(() => toast.error("Failed to load questions"));
  }, [formData.type, auth?.token]);

  const inputHandler = (e) => {
    const { name, value } = e.target;
    if (name === "type" && !isEdit) {
      setFormData({
        type: value,
        parent_id: "",
        name: "",
        description: "",
        category_color: "",
        status: 1,
      });
      setErrors({});
      return;
    }
    setFormData({ ...formData, [name]: value });
  };

  const handleSubmit = (e) => {
    e.preventDefault();
    setLoading(true);
    setErrors({});

    const payload = {
      name: formData.name,
      description: formData.description,
      category_color: formData.category_color,
      status: formData.status,
    };

    if (needsParent(formData.type)) {
      payload.parent_id = formData.parent_id;
    }

    const request = isEdit
      ? axios.put(`/api/admin/v1/review-category/${id}`, payload, {
          headers: {
            "Content-Type": "application/json",
            Authorization: `Bearer ${auth?.token}`,
          },
        })
      : axios.post(
          "/api/admin/v1/add-review-category",
          {
            ...payload,
            type: formData.type,
          },
          {
            headers: {
              "Content-Type": "application/json",
              Authorization: `Bearer ${auth?.token}`,
            },
          }
        );

    request
      .then((res) => {
        toast.success(
          res.data?.message ||
            (isEdit ? "Updated successfully" : "Added successfully")
        );
        navigate("/review-category");
      })
      .catch((err) => {
        const data = err.response?.data;
        if (typeof data === "object" && data !== null) {
          setErrors(data);
          toast.error(data.message || "Something went wrong");
        } else {
          toast.error("Something went wrong");
        }
      })
      .finally(() => setLoading(false));
  };

  const showParent = needsParent(formData.type);
  const showNameAndDescription = Boolean(formData.type);

  if (fetching) {
    return (
      <div className="text-center py-5">
        <Spinner color="light" />
      </div>
    );
  }

  return (
    <div>
      <div className="row my-3">
        <div className="col-md-12">
          <h4 className="text-white">
            {isEdit ? "Edit Review Category" : "Add Review Category"}
          </h4>
        </div>
      </div>
      <div className="bg-white px-3 py-3 rounded-2 my-4">
        <form onSubmit={handleSubmit}>
          <div className="row">
            <div className="col-md-6 offset-md-3 mb-3">
              <FormGroup>
                <Label>
                  Review Category Type{" "}
                  <span className="text-danger fs-4">*</span>
                </Label>
                <Input
                  type="select"
                  name="type"
                  value={formData.type}
                  onChange={inputHandler}
                  invalid={Boolean(errors?.type)}
                  disabled={isEdit}
                >
                  <option value="">Select type</option>
                  {reviewCategoryType.map((item) => (
                    <option key={item.value} value={item.value}>
                      {item.name}
                    </option>
                  ))}
                </Input>
                <span className="d-block text-danger">{errors?.type}</span>
              </FormGroup>
            </div>

            {showParent && (
              <div className="col-md-6 offset-md-3 mb-3">
                <FormGroup>
                  <Label>
                    Question <span className="text-danger fs-4">*</span>
                  </Label>
                  <Input
                    type="select"
                    name="parent_id"
                    value={formData.parent_id}
                    onChange={inputHandler}
                    invalid={Boolean(errors?.parent_id)}
                  >
                    <option value="">Select question</option>
                    {questions.map((q) => (
                      <option key={q.id} value={q.id}>
                        {q.name}
                      </option>
                    ))}
                  </Input>
                  <span className="d-block text-danger">
                    {errors?.parent_id || errors?.message}
                  </span>
                </FormGroup>
              </div>
            )}

            {showNameAndDescription && (
              <>
                <div className="col-md-6 offset-md-3 mb-3">
                  <FormGroup>
                    <Label>
                      {nameLabels[formData.type] || "Name"}{" "}
                      <span className="text-danger fs-4">*</span>
                    </Label>
                    <Input
                      name="name"
                      value={formData.name}
                      onChange={inputHandler}
                      invalid={Boolean(errors?.name)}
                    />
                    <span className="d-block text-danger">{errors?.name}</span>
                  </FormGroup>
                </div>
                {formData.type !== "question" &&
                  formData.type !== "spamQuestion" && (
                    <div className="col-md-6 offset-md-3 mb-3">
                      <FormGroup>
                        <Label>Category Color</Label>
                        <Input
                          name="category_color"
                          value={formData.category_color}
                          onChange={inputHandler}
                          invalid={Boolean(errors?.category_color)}
                        />
                        <span className="d-block text-danger">
                          {errors?.category_color}
                        </span>
                      </FormGroup>
                    </div>
                  )}
                <div className="col-md-6 offset-md-3 mb-3">
                  <FormGroup>
                    <Label>Description</Label>
                    <Input
                      type="textarea"
                      name="description"
                      value={formData.description}
                      onChange={inputHandler}
                      rows={4}
                    />
                  </FormGroup>
                </div>
                <div className="col-md-6 offset-md-3 mb-3">
                  <FormGroup check>
                    <Input
                      type="checkbox"
                      name="status"
                      checked={Number(formData.status) === 1}
                      onChange={(e) =>
                        setFormData({
                          ...formData,
                          status: e.target.checked ? 1 : 0,
                        })
                      }
                    />
                    <Label check>Active</Label>
                  </FormGroup>
                </div>
              </>
            )}

            <div className="col-md-6 offset-md-3">
              <Button
                color="primary"
                type="submit"
                disabled={loading || !formData.type}
              >
                {loading
                  ? "Saving..."
                  : isEdit
                    ? "Save Changes"
                    : "Submit"}
              </Button>
              <Button
                color="secondary"
                className="ms-2"
                type="button"
                onClick={() => navigate("/review-category")}
              >
                Cancel
              </Button>
            </div>
          </div>
        </form>
      </div>
    </div>
  );
};

export default AddReviewCategory;
